import React, { useEffect, useState, useRef } from 'react';
import "./Input.scss";
import { IMaskInput, IMask } from 'react-imask';

const Input = ({ defaultValue = '', inputHandler = null, type = null, label, inputType = 'text', resetKey, errorMessage = '' }) => {
    const ref = useRef(null);
    const inputRef = useRef(null);

    const [rawValue, setRawValue] = useState(defaultValue);
    const [value, setValue] = useState(
        defaultValue !== '' && (type === 'monthly_hours' || type === 'position')
            ? formatDisplayValue(defaultValue)
            : defaultValue !== ''
                ? defaultValue
                : ''
    );

    const [active, setActive] = useState(defaultValue != '');
    
    const [isFocused, setIsFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    function formatCounterMeasure(count, word) {
        if (!count && type === 'monthly_hours') return '';
        if (!count) return 0 + ' ' + word + 'ов';
        count = Number(count);
        if (isNaN(count) && type === 'monthly_hours') return '';
        if (isNaN(count)) return 0 + ' ' + word + 'ов';

        const lastTwo = count % 100;
        if (lastTwo >= 11 && lastTwo <= 14) return count + ' ' + word + 'ов';

        const last = count % 10;
        if (last === 1) return count + ' ' + word;
        if (last >= 2 && last <= 4) return count + ' ' + word + 'а';

        return count + ' ' + word + 'ов';
    }

    function formatDisplayValue(val, word) {
        const number = parseInt(val, 10);
        return `${formatCounterMeasure(number, word)}`;
    }

    useEffect(() => {
        if (type === 'monthly_hours') {
            setRawValue(defaultValue);
            setValue(formatCounterMeasure(defaultValue, 'час'));
        } else if (type === 'position') {
            setRawValue(defaultValue);
            setValue(formatCounterMeasure(defaultValue, 'специалист'));
        } else {
            setValue(defaultValue);
        }
        setActive(defaultValue !== '');
    }, [defaultValue, type]);

    const onChange = (e) => {
        let val = e.target.value;

        if (type === 'monthly_hours' || type === 'position') {
            if (isFocused) {
                val = val.replace(/\D/g, '');
                setRawValue(val);
                setValue(val);
            } else {
                const numericVal = val.replace(/\D/g, '');
                setRawValue(numericVal);
                setValue(formatDisplayValue(numericVal, type === 'monthly_hours' ? 'час' : 'специалист'));
            }

            if (inputHandler) inputHandler(val);
        } else if (type === 'days') {
            if (isFocused) {
                val = val.replace(/\D/g, '');
                setRawValue(val);
                setValue(val);
            } else {
                const numericVal = val.replace(/\D/g, '');
                setRawValue(numericVal);
                setValue(numericVal);
            }

            if (inputHandler) inputHandler(val);
        } else {
            setValue(val);
            if (inputHandler) inputHandler(val);
        }
    };

    const onFocus = () => {
        setIsFocused(true);
        setActive(true);
        if (type === 'monthly_hours' || type === 'position' || type === 'days') {
            setValue(rawValue || '');
            inputHandler(rawValue || '');
        }
    };

    const onBlur = () => {
        setIsFocused(false);
        if (type === 'monthly_hours' || type === 'position') {
            setValue(formatDisplayValue(rawValue, type === 'monthly_hours' ? 'час' : 'специалист'));
        }
        if (type === 'days') {
            setValue(rawValue);
        }
        if (value === '' && type !== 'monthly_hours' && type !== 'position' && type !== 'days') {
            setActive(false);
        }
    };

    const onClickLabel = () => {
        if (inputRef.current) {
            inputRef.current.focus();
            setActive(true);
        }
    };

    useEffect(() => {
        if (type === 'days' && resetKey) {
            setValue('');
            setRawValue('');
            inputRef.current.value = '';
            setActive(false);
        }
    }, [resetKey]);

    return (
        <div className={"input" + (active ? " active" : '') + (type ? ` ${type}` : '')}>
            <label onClick={onClickLabel}>{label}</label>

            {type === 'phone' && (
                <IMaskInput
                    mask="+7 (000) 000-00-00"
                    radix="."
                    value={value}
                    unmask={false}
                    ref={ref}
                    inputRef={inputRef}
                    onAccept={(val, mask) => {
                        if (val === '+7 (8') val = '+7 (';
                        if (val !== value) {
                            setValue(val);
                            if (inputHandler) inputHandler(val);
                        }
                    }}
                    onFocus={() => setActive(true)}
                    onBlur={() => {
                        if (value === '') setActive(false);
                    }}
                />
            )}

            {type === 'date' && (
                <IMaskInput
                    mask="00.00.0000"
                    radix="."
                    value={value}
                    unmask={false}
                    ref={ref}
                    inputRef={inputRef}
                    onAccept={(val, mask) => {
                        if (val !== value) {
                            setValue(val);
                            if (inputHandler) inputHandler(val);
                        }
                    }}
                    onFocus={() => setActive(true)}
                    onBlur={() => {
                        if (value === '') setActive(false);
                    }}
                />
            )}

            {type === 'date_birth' && (
                <IMaskInput
                    radix="."
                    value={value}
                    unmask={false}
                    ref={ref}
                    inputRef={inputRef}
                    onAccept={(val, mask) => {
                        if (val !== value) {
                            setValue(val);
                            if (inputHandler) inputHandler(val);
                        }
                    }}
                    onFocus={() => setActive(true)}
                    onBlur={() => {
                        if (value === '') setActive(false);
                    }}
                    mask="DD.MM.YYYY"
                    blocks={{
                        YYYY: {
                            mask: IMask.MaskedRange,
                            from: new Date().getFullYear() - 110,
                            to: new Date().getFullYear()
                        },
                        MM: {
                            mask: IMask.MaskedRange,
                            from: 1,
                            to: 12
                        },
                        DD: {
                            mask: IMask.MaskedRange,
                            from: 1,
                            to: 31
                        }
                    }}
                />
            )}

            {type === 'monthly_hours' && (
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />
            )}

            {type === 'days' && (
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />
            )}

            {type === 'position' && (
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />
            )}

            {!type && (
                <input
                    ref={inputRef}
                    type={inputType}
                    value={value}
                    onFocus={() => setActive(true)}
                    onBlur={() => {
                        if (value === '') setActive(false);
                    }}
                    onChange={onChange}
                />
            )}

            {type === 'password' &&
                <>
                    <input ref={inputRef} type={showPassword ? 'text' : 'password'} value={value}
                        onFocus={() => setActive(true)}
                        onBlur={() => { if (value === '') setActive(false) }}
                        onChange={onChange} />
                    <button className={'password-btn' + (showPassword ? ' show' : ' hide')} onClick={() => setShowPassword(!showPassword)}></button>
                </>
            }

            {errorMessage !== '' &&
                <div className="input__error">
                    {errorMessage}
                </div>
            }
        </div>
    );
};

export default Input;